import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { useLagoon } from '../../../hooks/useLagoon/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText/useNotAvailableUnknownText';
import { PhoneNumbers } from '../../../models/ProviderDetails';
import {
  getFormattedPhoneNumber,
  getPrimaryOrAfterHoursPhone,
} from '../../../utils/phone.utils';
import { AfterHoursPopoverIcon } from './AfterHoursPopoverIcon';
import {
  DirectoryPhoneContent,
  DirectoryPhoneItem,
} from './DirectoryPhoneNumbers.styled';
import {
  IconMaterialContainer,
  PhoneAllContainer,
  PhoneContainer,
  PhoneNumberContainer,
  infoSubTextContainer,
} from './ProviderLocationsDetails.style';

type Props = {
  phoneNumbers?: PhoneNumbers | null;
  tty?: string;
  isFromVirtualProviderGroup?: boolean;
  showAdditionalPhones?: boolean;
  showExamPhone?: boolean;
  isCNSNJPlanMember?: boolean;
  enableAfterHoursPhone?: boolean;
};

type PhoneEntry = {
  type: string;
  numbers: string[] | null;
};

function toPhoneArray(numbers: string[] | null | undefined): string[] | null {
  return numbers && numbers.length > 0 ? numbers : null;
}

function buildAfterHoursEntry(
  afterHoursObj: ReturnType<typeof getPrimaryOrAfterHoursPhone>,
  afterHours: string[] | null | undefined,
  label: string
): PhoneEntry {
  if (!afterHoursObj?.value) {
    return { type: label, numbers: null };
  }

  const hasAfterHoursArray = afterHours && afterHours.length > 0;
  const numbers = hasAfterHoursArray
    ? afterHours
    : [afterHoursObj.value as string];

  return { type: label, numbers };
}

function buildPhoneEntries(
  phoneNumbers: PhoneNumbers,
  tty: string | undefined,
  options: {
    showAdditionalPhones: boolean;
    showExamPhone: boolean;
    isCNSNJPlanMember: boolean;
    enableAfterHoursPhone: boolean;
    isFromVirtualProviderGroup: boolean;
  },
  afterHoursObj: ReturnType<typeof getPrimaryOrAfterHoursPhone>,
  t: (key: string) => string
): PhoneEntry[] {
  const { phone, appointment, emergency, afterHours, fax } = phoneNumbers;
  const entries: PhoneEntry[] = [];

  entries.push({
    type: t('PROVIDER_LOCATIONS_DETAILS.GENERAL_PHONE'),
    numbers: toPhoneArray(phone),
  });

  if (options.showAdditionalPhones) {
    entries.push({
      type: t('PROVIDER_LOCATIONS_DETAILS.APPOINTMENT_PHONE'),
      numbers: toPhoneArray(appointment),
    });
    entries.push({
      type: t('PROVIDER_LOCATIONS_DETAILS.EMERGENCY_PHONE'),
      numbers: toPhoneArray(emergency),
    });
  }

  if (options.showExamPhone) {
    entries.push({
      type: t('OTHER_CARE_DETAILS.EXAM_PHONE'),
      numbers: toPhoneArray(emergency),
    });
  }

  if (options.enableAfterHoursPhone) {
    entries.push(
      buildAfterHoursEntry(
        afterHoursObj,
        afterHours,
        t('PROVIDER_LOCATIONS_DETAILS.AFTER_HOURS_PHONE')
      )
    );
  }

  if (!options.isFromVirtualProviderGroup) {
    entries.push({
      type: t('PROVIDER_LOCATIONS_DETAILS.TTY'),
      numbers: tty ? [tty] : null,
    });
  }

  if (options.isCNSNJPlanMember) {
    entries.push({
      type: t('SHARE_PDF.Fax'),
      numbers: toPhoneArray(fax),
    });
  }

  return entries;
}

export function DirectoryPhoneNumbers({
  phoneNumbers,
  tty,
  isFromVirtualProviderGroup,
  showAdditionalPhones = true,
  showExamPhone = false,
  isCNSNJPlanMember = false,
  enableAfterHoursPhone = true,
}: Props) {
  const { t } = useTranslation();
  const lagoonData = useLagoon('ui-messaging')();
  const [isAfterHoursPopoverOpen, setIsAfterHoursPopoverOpen] = useState(false);

  if (!phoneNumbers) {
    return null;
  }

  const {
    headingStyle,
    descriptionStyle,
    providerLocations: notAvailableOrUnknownText,
  } = useNotAvailableUnknownText();

  const afterHoursInfoPopoverContent = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.ABOUT_AFTER_HOURS_CONTENT,
  });

  const afterHoursObj = getPrimaryOrAfterHoursPhone(phoneNumbers);
  const isAfterHoursReal = afterHoursObj?.isAfterHours;
  const shouldShowAfterHoursInfoIcon =
    enableAfterHoursPhone && afterHoursObj && !isAfterHoursReal;

  const phoneEntries = buildPhoneEntries(
    phoneNumbers,
    tty,
    {
      showAdditionalPhones,
      showExamPhone,
      isCNSNJPlanMember,
      enableAfterHoursPhone,
      isFromVirtualProviderGroup: isFromVirtualProviderGroup || false,
    },
    afterHoursObj,
    t
  );

  const fontSize = {
    fontSize: '18px',
  };

  const width = {
    width: '180px',
  };

  const isAfterHoursEntry = (type: string) =>
    type === t('PROVIDER_LOCATIONS_DETAILS.AFTER_HOURS_PHONE');

  // Show ALL phone entries with ALL numbers - no collapsing in drawer
  return (
    <DirectoryPhoneContent data-testid="directory-phone-numbers" role="list">
      {phoneEntries.map((entry, entryIndex) => (
        <DirectoryPhoneItem key={`${entry.type}-${entryIndex}`} role="listitem">
          <PhoneAllContainer>
            <IconMaterialContainer color="$primary1" icon="call" size={18} />
            <PhoneContainer css={headingStyle}>
              {isAfterHoursEntry(entry.type) && shouldShowAfterHoursInfoIcon ? (
                <span
                  style={{
                    ...headingStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {entry.type}
                  <AfterHoursPopoverIcon
                    content={afterHoursInfoPopoverContent?.message}
                    isOpen={isAfterHoursPopoverOpen}
                    setIsOpen={setIsAfterHoursPopoverOpen}
                  />
                </span>
              ) : (
                <span style={headingStyle}>{entry.type}</span>
              )}
              <PhoneNumberContainer
                style={{
                  ...width,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  marginTop: '4px',
                }}
              >
                {entry.numbers && entry.numbers.length > 0 ? (
                  entry.numbers.map((phoneNumber, numIndex) => (
                    <a
                      data-testid={`directory-phone-${entryIndex}-${numIndex}`}
                      href={`tel:${phoneNumber}`}
                      key={`${phoneNumber}-${numIndex}`}
                      style={fontSize}
                    >
                      {getFormattedPhoneNumber(phoneNumber)}
                    </a>
                  ))
                ) : (
                  <div
                    data-testid={`directory-phone-${entryIndex}-not-available`}
                  >
                    <Text
                      css={{
                        ...infoSubTextContainer(false),
                        ...descriptionStyle,
                      }}
                    >
                      {notAvailableOrUnknownText}
                    </Text>
                  </div>
                )}
              </PhoneNumberContainer>
            </PhoneContainer>
          </PhoneAllContainer>
        </DirectoryPhoneItem>
      ))}
    </DirectoryPhoneContent>
  );
}
